// =============================================================================
// AuthHero — the left "art piece" panel shared by Login and Signup.
//
// Inspired by Stripe's homepage gradient ribbon. Implementation uses 5
// blurred coloured blobs over a deep slate backdrop, each animated on its
// own slightly-out-of-sync cycle so they morph into a flowing ribbon-like
// effect without aligning periodically. mix-blend-mode: screen lets the
// colours blend optically the way light does, which is what gives the
// "rainbow of light" feel rather than "stack of paint."
//
// All animation is pure CSS — no JS frame loop, no canvas, no WebGL. The
// 5 blob keyframes live in globals.css.
//
// Hidden under `lg`; the auth pages render the form full-width on mobile.
// =============================================================================

import type { ReactNode } from 'react';

interface AuthHeroProps {
  headline: ReactNode;
  subheadline: ReactNode;
}

export function AuthHero({ headline, subheadline }: AuthHeroProps) {
  return (
    <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden">
      {/* Deep base — gives the gradient blobs a dark canvas to glow against */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />

      {/* Animated mesh gradient — five blurred blobs, each on a different
          loop so the composition never quite repeats. mix-blend-screen
          lets the colours add the way light does. pointer-events-none
          so the form panel stays interactive. */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-24 w-[28rem] h-[28rem] rounded-full bg-violet-500 mix-blend-screen blur-[100px] opacity-70 animate-auth-blob-a" />
        <div className="absolute top-1/4 -right-40 w-[32rem] h-[32rem] rounded-full bg-teal-400 mix-blend-screen blur-[100px] opacity-70 animate-auth-blob-b" />
        <div className="absolute -bottom-40 left-1/4 w-[36rem] h-[36rem] rounded-full bg-rose-500 mix-blend-screen blur-[100px] opacity-60 animate-auth-blob-c" />
        <div className="absolute top-1/2 left-1/3 w-[26rem] h-[26rem] rounded-full bg-amber-400 mix-blend-screen blur-[120px] opacity-50 animate-auth-blob-d" />
        <div className="absolute -top-16 right-1/4 w-[22rem] h-[22rem] rounded-full bg-emerald-400 mix-blend-screen blur-[100px] opacity-50 animate-auth-blob-e" />

        {/* Subtle grain so the gradient doesn't look plastic on retina */}
        <div className="absolute inset-0 opacity-[0.04] mix-blend-overlay"
          style={{
            backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.9 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")`,
          }}
        />
      </div>

      {/* Foreground content — logo, headline, copyright */}
      <div className="relative z-10 flex flex-col justify-between p-12 w-full text-white">
        <div className="flex items-center gap-3">
          <img src="/logo.svg" alt="DiscoveryGem" className="w-10 h-10 brightness-0 invert" />
          <div>
            <div className="font-semibold text-lg leading-none mb-1">DiscoveryGem</div>
            <div className="text-white/60 text-[10px] uppercase tracking-[0.2em]">Platform</div>
          </div>
        </div>

        <div>
          <h2 className="text-3xl font-bold leading-tight mb-4 drop-shadow-sm">
            {headline}
          </h2>
          <p className="text-white/75 text-sm leading-relaxed max-w-md">
            {subheadline}
          </p>
        </div>

        <div className="text-white/45 text-xs">
          &copy; 2026 Arrive Tech LLC. All rights reserved.
        </div>
      </div>
    </div>
  );
}
